import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { sprayService, SprayMachine, SprayMachineInput } from '../../services/sprayService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useFormDirty } from '../../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { useConfirmDiscard } from '../../hooks/useConfirmDiscard';
import PageHeader from '../shell/PageHeader';
import { Field, FormSection, FormShell, SaveBar, SaveBarStatus } from '../patterns';

interface Props {
  machine?: SprayMachine;
  onSave: (machine: SprayMachine) => void;
  onCancel: () => void;
}

const numberOrEmpty = (n?: number): string => (n === undefined || n === null ? '' : String(n));

const parseNumberOrUndefined = (s: string): number | undefined => {
  const trimmed = s.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const SprayMachineForm: React.FC<Props> = ({ machine, onSave, onCancel }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(machine);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.machines.title'), to: '/protection/machines' },
    {
      label: isEdit
        ? t('spray.machines.editTitle', { name: machine?.name, defaultValue: 'Edit machine' })
        : t('spray.pageHeader.actions.newMachine'),
    },
  ]);

  const [name, setName] = useState(machine?.name ?? '');
  const [tractorName, setTractorName] = useState(machine?.tractorName ?? '');
  const [sprayerName, setSprayerName] = useState(machine?.sprayerName ?? '');
  const [tankVolume, setTankVolume] = useState(numberOrEmpty(machine?.tankVolume));
  const [engineRevs, setEngineRevs] = useState(numberOrEmpty(machine?.engineRevs));
  const [pto, setPto] = useState(machine?.pto ?? '');
  const [nozzlesConfig, setNozzlesConfig] = useState(machine?.nozzlesConfig ?? '');
  const [nameError, setNameError] = useState('');
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);

  const { dirty } = useFormDirty(
    {
      name: machine?.name ?? '',
      tractorName: machine?.tractorName ?? '',
      sprayerName: machine?.sprayerName ?? '',
      tankVolume: numberOrEmpty(machine?.tankVolume),
      engineRevs: numberOrEmpty(machine?.engineRevs),
      pto: machine?.pto ?? '',
      nozzlesConfig: machine?.nozzlesConfig ?? '',
    },
    { name, tractorName, sprayerName, tankVolume, engineRevs, pto, nozzlesConfig },
  );
  useBeforeUnloadWarning(dirty);
  const confirmDiscard = useConfirmDiscard();

  const handleSave = async () => {
    setNameError('');
    setApiError('');
    if (!name.trim()) {
      setNameError(t('spray.machines.nameRequired'));
      return;
    }
    setSaving(true);
    const data: SprayMachineInput = {
      name: name.trim(),
      tractorName: tractorName.trim() || undefined,
      sprayerName: sprayerName.trim() || undefined,
      tankVolume: parseNumberOrUndefined(tankVolume),
      engineRevs: parseNumberOrUndefined(engineRevs),
      pto: pto.trim() || undefined,
      nozzlesConfig: nozzlesConfig.trim() || undefined,
    };
    try {
      const result = machine
        ? await sprayService.updateMachine(machine._id, data)
        : await sprayService.createMachine(data);
      onSave(result);
    } catch (err: any) {
      const serverError = err?.response?.data?.error;
      setApiError(serverError ?? err?.message ?? t('spray.machines.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSave();
  };

  return (
    <div data-testid="spray-machine-form">
      <PageHeader
        title={
          isEdit
            ? t('spray.machines.editTitle', { name: machine?.name, defaultValue: 'Edit machine' })
            : t('spray.pageHeader.actions.newMachine')
        }
        meta={t('spray.machines.formMeta', { defaultValue: 'Machine identity and tractor pairing.' })}
      />

      {apiError && (
        <Alert variant="danger" role="alert" className="mb-3">
          {apiError}
        </Alert>
      )}

      <FormShell
        sections={[]}
        onSubmit={handleSubmit}
        saveBar={
          <SaveBar
            status={
              saving
                ? ({ kind: 'saving' } as SaveBarStatus)
                : apiError
                  ? ({ kind: 'error', message: apiError } as SaveBarStatus)
                  : dirty
                    ? ({ kind: 'dirty' } as SaveBarStatus)
                    : null
            }
            primaryAction={{
              label: saving
                ? t('patterns.saveBar.primary.saving')
                : apiError
                  ? t('patterns.saveBar.primary.retry')
                  : t('patterns.saveBar.primary.save'),
              onClick: handleSave,
              disabled: (!dirty && !apiError) || saving,
            }}
            secondaryActions={[
              { label: t('common.cancel'), tone: 'subtle', onClick: () => { if (confirmDiscard(dirty)) onCancel(); }, disabled: saving },
            ]}
          />
        }
      >
        <FormSection
          id="machine-identity"
          title={t('spray.machines.sections.identity', { defaultValue: 'Identity' })}
        >
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('spray.machines.name')}
              required
              controlId="machine-name"
              errorText={nameError || undefined}
            >
              <input
                id="machine-name"
                type="text"
                className="pat-input"
                value={name}
                onChange={e => setName(e.target.value)}
                aria-invalid={Boolean(nameError) || undefined}
                aria-required="true"
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          id="machine-configuration"
          title={t('spray.machines.sections.configuration', { defaultValue: 'Configuration' })}
        >
          <div className="pat-field-grid pat-field-grid--2">
            <Field label={t('spray.machines.tractorName')} controlId="machine-tractor-name">
              <input
                id="machine-tractor-name"
                type="text"
                className="pat-input"
                value={tractorName}
                onChange={e => setTractorName(e.target.value)}
              />
            </Field>
            <Field label={t('spray.machines.sprayerName')} controlId="machine-sprayer-name">
              <input
                id="machine-sprayer-name"
                type="text"
                className="pat-input"
                value={sprayerName}
                onChange={e => setSprayerName(e.target.value)}
              />
            </Field>
            <Field label={t('spray.machines.tankVolume')} controlId="machine-tank-volume">
              <input
                id="machine-tank-volume"
                type="number"
                min={0}
                step="any"
                className="pat-input"
                value={tankVolume}
                onChange={e => setTankVolume(e.target.value)}
              />
            </Field>
            <Field label={t('spray.machines.engineRevs')} controlId="machine-engine-revs">
              <input
                id="machine-engine-revs"
                type="number"
                min={0}
                step="any"
                className="pat-input"
                value={engineRevs}
                onChange={e => setEngineRevs(e.target.value)}
              />
            </Field>
            <Field label={t('spray.machines.pto')} controlId="machine-pto">
              <input
                id="machine-pto"
                type="text"
                className="pat-input"
                value={pto}
                onChange={e => setPto(e.target.value)}
              />
            </Field>
            <Field label={t('spray.machines.nozzlesConfig')} controlId="machine-nozzles-config">
              <input
                id="machine-nozzles-config"
                type="text"
                className="pat-input"
                value={nozzlesConfig}
                onChange={e => setNozzlesConfig(e.target.value)}
              />
            </Field>
          </div>
        </FormSection>
      </FormShell>
    </div>
  );
};

export default SprayMachineForm;
